// client/src/pages/NoticePage.js
// 공지사항 — v5 리디자인 (B안 확정: 중요 공지 상단 고정 + 월별 그룹 플랫 리스트 + 대상 필터)
// 조회·캐시·페이지네이션 로직은 기존 그대로.
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import InfluencerHomeHeader from '../components/influencer/InfluencerHomeHeader';
import MarketerHomeHeader from '../components/marketer/MarketerHomeHeader';
import { PointProvider } from '../contexts/PointContext';
import Footer from '../components/landing/Footer';
import '../styles/public/pub.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function RoleHeader() {
  const loc = useLocation();
  const [role, setRole] = useState(() => {
    try { return loc.state?.role || sessionStorage.getItem('current_user_role'); } catch { return null; }
  });

  useEffect(() => {
    const hint = loc.state?.role;
    if (hint) {
      setRole(hint);
      try { sessionStorage.setItem('current_user_role', hint); } catch {}
    }
  }, [loc.state]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/me`, { credentials: 'include' });
        const j = await res.json().catch(()=>({}));
        if (!alive) return;
        const r = (res.ok && j?.ok && j?.user?.role) ? j.user.role : null;
        if (r) {
          setRole(r);
          try { sessionStorage.setItem('current_user_role', r); } catch {}
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  if (role === 'influencer') {
    return (
      <PointProvider userRole="influencer">
        <InfluencerHomeHeader />
      </PointProvider>
    );
  }
  if (role === 'marketer') {
    return (
      <PointProvider userRole="marketer">
        <MarketerHomeHeader />
      </PointProvider>
    );
  }
  return <Header />;
}

const KIND_BADGE = {
  all: { label: '전체', cls: 'badge--gray' },
  marketer: { label: '광고주', cls: 'badge--blue' },
  influencer: { label: '인플루언서', cls: 'badge--mint' },
};

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'marketer', label: '광고주' },
  { key: 'influencer', label: '인플루언서' },
];

function NoticePage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const pageFromUrl = Math.max(1, Number(sp.get('page') || 1));
  const pageSize = 15;
  const cacheKey = `notice_p${pageFromUrl}`;

  const [items, setItems] = useState(() => {
    try { return (JSON.parse(localStorage.getItem(cacheKey) || '{}').items) || []; } catch { return []; }
  });
  const [total, setTotal] = useState(() => {
    try { return Number(JSON.parse(localStorage.getItem(cacheKey) || '{}').total) || 0; } catch { return 0; }
  });
  const [page, setPage] = useState(pageFromUrl);
  const [loading, setLoading] = useState(() => !localStorage.getItem(cacheKey));
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const fetchList = async (p) => {
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/notices?page=${p}&pageSize=${pageSize}`, { credentials: 'include' });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || `불러오기 실패(${res.status})`);
      setItems(Array.isArray(j.items) ? j.items : []);
      setTotal(Number(j.total || 0));
      try { localStorage.setItem(`notice_p${p}`, JSON.stringify(j)); } catch {}
    } catch (e) {
      setErr(e.message || '불러오기 실패');
    }
    setLoading(false);
  };

  useEffect(() => { fetchList(page); }, [page]);

  const goPage = (p) => {
    const np = Math.min(Math.max(1, p), totalPages);
    setPage(np);
    setSp({ page: String(np) });
  };

  const goDetail = (n) =>
    navigate(`/notice/${n._id}`, {
      state: { meta: n, role: sessionStorage.getItem('current_user_role') }
    });

  const fmtShort = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // 필터 (현재 페이지 내 대상 필터) → 중요 고정 + 월별 그룹
  const { pinned, monthGroups } = useMemo(() => {
    const visible = filter === 'all' ? items : items.filter((n) => (n.kind || 'all') === filter || (n.kind || 'all') === 'all');
    const pin = visible.filter((n) => n.important);
    const rest = visible.filter((n) => !n.important);
    const groups = [];
    rest.forEach((n) => {
      const d = new Date(n.createdAt);
      const key = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(n);
      else groups.push({ key, items: [n] });
    });
    return { pinned: pin, monthGroups: groups };
  }, [items, filter]);

  const pageButtons = () => {
    const delta = 2;
    const start = Math.max(1, page - delta);
    const end = Math.min(totalPages, page + delta);
    const buttons = [];
    for (let i = start; i <= end; i++) buttons.push(i);
    return buttons;
  };

  const renderRow = (n, inPin) => {
    const badge = n.important
      ? { label: '중요', cls: 'badge--red' }
      : (KIND_BADGE[n.kind || 'all'] || KIND_BADGE.all);
    return (
      <button key={n._id} type="button" className="nlist__row" onClick={() => goDetail(n)}>
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
        <span className="t">{inPin ? <b>{n.title}</b> : n.title}</span>
        <span className="d num">{fmtShort(n.createdAt)}</span>
      </button>
    );
  };

  return (
    <div className="pub">
      <RoleHeader />
      <main className="wrap">
        <div className="pub-head">
          <h1>공지사항</h1>
          <p>셀프애드의 새로운 소식을 알려드려요.</p>
        </div>

        <div className="notice">
          <div className="notice-filter">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={filter === f.key ? 'is-active' : ''}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
              불러오는 중...
            </div>
          )}
          {err && <div className="note note--red">{err}</div>}

          {!loading && !err && pinned.length === 0 && monthGroups.length === 0 && (
            <div className="card" style={{ padding: '56px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>등록된 공지가 없어요</div>
              <div className="t-sub">새로운 소식이 올라오면 여기에 표시돼요.</div>
            </div>
          )}

          {!loading && !err && (pinned.length > 0 || monthGroups.length > 0) && (
            <div className="nlist">
              {pinned.length > 0 && (
                <div className="nlist__pin">
                  {pinned.map((n) => renderRow(n, true))}
                </div>
              )}
              {monthGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <span className="nlist__month">{g.key}</span>
                  {g.items.map((n) => renderRow(n, false))}
                </React.Fragment>
              ))}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="pager">
              <button type="button" onClick={() => goPage(page - 1)} disabled={page <= 1}>‹</button>
              {pageButtons().map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goPage(p)}
                  className={p === page ? 'is-active' : ''}
                >
                  {p}
                </button>
              ))}
              <button type="button" onClick={() => goPage(page + 1)} disabled={page >= totalPages}>›</button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default NoticePage;
